/* eslint-disable no-lonely-if */
/* eslint-disable prefer-destructuring */
import React, { forwardRef, useRef, useLayoutEffect, useEffect } from 'react';
import PropTypes from 'prop-types';

const KEYCODE_TAB = 9;

// eslint-disable-next-line react/display-name
const FocusTrap = forwardRef(
  ({ tag, className, children, delayFocus, hasFocus }, ref) => {
    const container = useRef(null);
    const focussableElements = useRef([]);
    const firstEl = useRef([]);
    const lastEl = useRef([]);
    const isFocussed = useRef(false);

    const Tag = tag;

    const setFocusLastEl = () => {
      if (!isFocussed.current) isFocussed.current = true;
      lastEl.current.focus();
    };

    const setFocusFirstEl = () => {
      if (!isFocussed.current) isFocussed.current = true;
      firstEl.current.focus();
    };

    const handleFocusChange = e => {
      const isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;

      if (!isTabPressed) {
        return;
      }

      if (e.shiftKey) {
        if (document.activeElement === firstEl.current) {
          /* shift + tab */
          setFocusLastEl();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastEl.current) {
          /* tab */
          setFocusFirstEl();
          e.preventDefault();
        }
      }
    };

    useLayoutEffect(() => {
      const el = ref ? ref.current : container.current;
      const items = el.querySelectorAll(
        'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])'
      );
      focussableElements.current = [...items].filter(
        item => item.tabIndex !== -1
      );
      firstEl.current = focussableElements.current[0];
      lastEl.current =
        focussableElements.current[focussableElements.current.length - 1];

      document.addEventListener('keydown', handleFocusChange);

      if (!delayFocus) {
        setFocusFirstEl();
      }

      return () => {
        document.removeEventListener('keydown', handleFocusChange);
      };
    }, []);

    useEffect(() => {
      if (hasFocus) {
        setFocusFirstEl();
      }
    }, [hasFocus]);

    return (
      <Tag className={className} ref={ref || container}>
        {children}
      </Tag>
    );
  }
);

FocusTrap.defaultProps = {
  tag: 'div',
  className: '',
  delayFocus: false,
  hasFocus: false,
};

FocusTrap.propTypes = {
  children: PropTypes.node.isRequired,
  tag: PropTypes.string,
  className: PropTypes.string,
  delayFocus: PropTypes.bool,
  hasFocus: PropTypes.bool,
};

export default FocusTrap;
